import { TemplateFormat } from '@langchain/core/prompts';
import { XIcon } from '@langchain/untitled-ui-icons';
import * as RadixDialog from '@radix-ui/react-dialog';

import { useEffect, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { useSecrets } from '@/Pages/Playground/PlaygroundHome/PlaygroundSecretsContext.tsx';
import {
  EMessageRole,
  TArtifactLengthOptions,
  TReadingLevelOptions,
} from '@/hooks/promptCanvas/types.tsx';
import { usePromptCanvasAgentState } from '@/hooks/promptCanvas/usePromptCanvasAgent';
import { cn } from '@/utils/tailwind.tsx';
import { useLocalStorageState } from '@/utils/use-local-storage-state.tsx';

import CanvasPanel from './CanvasPanel.tsx';
import ChatPanel from './ChatPanel.tsx';
import { createHighlightFromIndexes } from './utils.ts';

const CANVAS_MODAL_PANEL_SIZE_STORAGE_KEY = 'ls:canvas-modal-panel-size';

const CanvasModal = ({
  isOpen,
  onClose,
  prompt,
  setPrompt,
  templateFormat,
}: {
  isOpen: boolean;
  onClose: () => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  templateFormat: TemplateFormat;
}) => {
  const [metaprompt, setMetaprompt] = useState('');

  const [panelSizes, setPanelSizes] = useLocalStorageState<number[]>(
    CANVAS_MODAL_PANEL_SIZE_STORAGE_KEY,
    [40, 60]
  );
  const { dedicatedOpenAISecret } = useSecrets();
  const openSecretsRef = useRef<(() => void) | null>(null);
  const needsOpenAIApiKey = !dedicatedOpenAISecret;

  const {
    messages,
    setMessages,
    artifact,
    setArtifact,
    selectedArtifactIndex,
    setSelectedArtifactIndex,
    viewingArtifactIndex,
    setViewingArtifactIndex,
    isCurrentPrompt,
    generate,
    isMutating,
    isFirstTokenReceived,
    showDiff,
    setShowDiff,
    promptToShow,
  } = usePromptCanvasAgentState({
    defaultPrompt: prompt,
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.stopPropagation();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown, {
        capture: true,
      });
    } else {
      window.removeEventListener('keydown', handleKeyDown, {
        capture: true,
      });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown, {
        capture: true,
      });
    };
  }, [isOpen]);

  const handleClose = (updateViewingArtifactIndex = true) => {
    setMetaprompt('');
    setShowDiff(false);
    if (updateViewingArtifactIndex) {
      setViewingArtifactIndex(selectedArtifactIndex);
    }
    onClose();
  };

  const handleOpenSecrets = () => {
    if (needsOpenAIApiKey) {
      openSecretsRef.current?.();
      return true;
    }
    return false;
  };

  const handleSubmit = ({
    improvement_instruction,
    highlightedRange,
    readingLevel,
    artifactLength,
    customAction,
  }: {
    improvement_instruction?: string;
    highlightedRange?: [number, number];
    readingLevel?: TReadingLevelOptions;
    artifactLength?: TArtifactLengthOptions;
    customAction?: string;
  }) => {
    if (handleOpenSecrets()) {
      return;
    }
    const highlighted = highlightedRange
      ? createHighlightFromIndexes(highlightedRange, promptToShow)
      : undefined;
    generate(
      {
        messages: improvement_instruction
          ? [
              ...messages,
              {
                type: EMessageRole.USER,
                content: improvement_instruction,
              },
            ]
          : messages,
        highlighted,
        artifact: {
          ...artifact,
          // always query against the artifact the user is viewing
          current_content_index: viewingArtifactIndex,
        },
        artifact_length: artifactLength,
        reading_level: readingLevel,
        custom_action: customAction,
        template_format: templateFormat,
        secrets: {
          OPENAI_API_KEY: dedicatedOpenAISecret!,
        },
      },
      {
        newArtifactIndex: artifact.contents.length,
        viewingArtifactIndex,
        viewingArtifactContent: promptToShow,
        highlightedRange: highlighted
          ? [
              highlighted.prompt_chunk_start_index,
              highlighted.prompt_chunk_end_index,
            ]
          : undefined,
      }
    );
    setMetaprompt('');
    if (improvement_instruction) {
      // Only add a new user message if they submitted a chat input
      setMessages((prev) => [
        ...prev,
        {
          type: EMessageRole.USER,
          content: improvement_instruction,
        },
      ]);
    }
  };

  // when opening the modal, update the artifact to the current prompt from playground
  useEffect(() => {
    if (isOpen) {
      setArtifact({
        ...artifact,
        contents: artifact.contents.map((content) => {
          if (content.index !== selectedArtifactIndex) {
            return content;
          }
          return {
            ...content,
            content: prompt,
          };
        }),
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <RadixDialog.Root open={isOpen} onOpenChange={onClose}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-10 flex h-screen items-center justify-center bg-black/30 dark:bg-black/40">
          <RadixDialog.Content
            className={cn(
              'relative mx-auto flex h-[700px] w-[1200px] flex-col items-stretch overflow-y-auto rounded-lg bg-primary shadow-xl'
            )}
          >
            <RadixDialog.Close className="absolute right-2 top-2 rounded-md p-2 hover:bg-tertiary">
              <XIcon className="size-6" />
            </RadixDialog.Close>
            <div className="h-full overflow-hidden">
              <PanelGroup direction="horizontal" onLayout={setPanelSizes}>
                <Panel defaultSize={panelSizes[0]} minSize={30}>
                  <ChatPanel
                    messages={messages}
                    metaprompt={metaprompt}
                    setMetaprompt={setMetaprompt}
                    isMutating={isMutating}
                    handleSubmit={handleSubmit}
                    openSecretsRef={openSecretsRef}
                  />
                </Panel>
                <PanelResizeHandle className="group relative flex items-stretch text-secondary data-[resize-handle-active]:text-ls-blue">
                  <div className="w-[2px] bg-tertiary group-hover:bg-brand-primary" />
                  <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
                  <div className="absolute inset-y-0 w-[2px] bg-current opacity-0 transition-all" />
                </PanelResizeHandle>
                <Panel defaultSize={panelSizes[1]} minSize={30}>
                  <CanvasPanel
                    isMutating={isMutating}
                    isFirstTokenReceived={isFirstTokenReceived}
                    handleSubmit={handleSubmit}
                    promptToShow={promptToShow}
                    artifact={artifact}
                    viewingArtifactIndex={viewingArtifactIndex}
                    selectedArtifactIndex={selectedArtifactIndex}
                    isCurrentPrompt={isCurrentPrompt}
                    setArtifact={setArtifact}
                    setPrompt={setPrompt}
                    setSelectedArtifactIndex={setSelectedArtifactIndex}
                    setViewingArtifactIndex={setViewingArtifactIndex}
                    showDiff={showDiff}
                    setShowDiff={setShowDiff}
                    handleClose={handleClose}
                    handleOpenSecrets={handleOpenSecrets}
                  />
                </Panel>
              </PanelGroup>
            </div>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default CanvasModal;
